<script setup lang="ts">
import NumberInPartyStep from "@/components/booking/steps/NumberInPartyStep.vue";
import DialogReceipt from "@/components/dialogReceipt.vue";
import EventStepDateTime from "@/components/events/AddEditDialog/EventStepDateTime.vue";
import EventStepDescription from "@/components/events/AddEditDialog/EventStepDescription.vue";
import EventStepOptions from "@/components/events/AddEditDialog/EventStepOptions.vue";
import EventStepRecurring from "@/components/events/AddEditDialog/EventStepRecurring.vue";
import EventStepTickets from "@/components/events/AddEditDialog/EventStepTickets.vue";
import { useEvent } from "@/components/events/useEvent";
import PaymentForm from "@/components/payment/PaymentForm.vue";
import { usePaymentStore } from "@/components/payment/usePaymentStore";
import { useListStore } from "@/store/listStore";
import { useDiscountCode } from "@/store/useDiscountCode";
import { useEventStore } from "@/store/useEventStore";
import { useGlobalErrorStore } from "@/store/useGlobalError";
import { getMembershipDiscounts } from "@/utils/BookingStepperUtil";
import { logDebug } from "@/utils/DebugLogs";
import { pb } from "@/utils/PocketBaseAdapter";
import { useProductStore } from "@/utils/ProductStore";
import { useUserStore } from "@/utils/UserStore";
import { moment } from "@/utils/useTimeZone";
import { cloneDeep, isEmpty } from "lodash";
import { ref } from "vue";
import { useRouter } from "vue-router";
import type { EventEmit, EventProps } from "../types";

// props
const props = defineProps<EventProps>();

// Emit
const emit = defineEmits<EventEmit>();

// composable
const globalErrorStore = useGlobalErrorStore();
const eventStore = useEventStore();
const paymentStore = usePaymentStore();
const discount = useDiscountCode();
const listStore = useListStore();
const productStore = useProductStore();
const userStore = useUserStore();

const { handleUpdateErrorMessage } = globalErrorStore;
const { fetchTotalSoldEvents } = eventStore;
const { fetchActivities } = listStore;

const { globalErrorMessage, isBookingWarningDialogVisible, errorDateList } = storeToRefs(globalErrorStore);

const { docketId } = storeToRefs(paymentStore);

const { currentLocationDetails } = storeToRefs(userStore);

const {
  eventId,
  eventBookingsId,
  recurringEventId,
  soldEvents,
} = storeToRefs(eventStore);

const { discountCode, discountCodeData } = storeToRefs(discount);
const { activityMap } = storeToRefs(listStore);

// composable
const events = useEvent();

const {
  reserveSlot,
  updateEvent,
  confirmDelete,
  confirmDeleteAllRecurring,
  removeExistingPendingBooking,
  refetchEvent,
  handleRecurringEvent,
  getTicketProducts,
} = events;

const {recurringWeeks} = events;


// Router
const router = useRouter();

// Refs
const dateTimeStepRef = ref<InstanceType<typeof EventStepDateTime>>();
const descriptionStepRef = ref<InstanceType<typeof EventStepDescription>>();
const recurringStepRef = ref<InstanceType<typeof EventStepRecurring>>();
const ticketsStepRef = ref<InstanceType<typeof EventStepTickets>>();
const optionsStepRef = ref<InstanceType<typeof EventStepOptions>>();
const numberInPartyStepRef = ref<any>(null);
const eventBookingsState = ref<any>("");
const totalSoldEvents = ref<number>(0);
const tempTicketsConfig = ref<any[]>([]);
const totalTicket = ref<number | string>(0);
const isLoading = ref<boolean>(false);
const dueAmount = ref<number>(0);
const eventTotal = ref<number>(0);
const userMembershipDiscounts = ref({});
// Store discount data per activity: { activityId: { todAdjustment, docketDiscount, discountApplied, lineDiscountTotal } }
const activityDiscounts = ref<Record<string, any>>({});

const createDefaultTicket = () => ({
  name: "",
  sellPrice: 0,
  sortOrder: 0,
  eventBarValue: 0,
  eventTicketLimit: 0,
  description: "",
  maxDiscount: 0,
  memberBookingDiscountApplies: false,
  notDirectlySold: false,
  canBuyInWebshop: false,
  canBuyInKiosk: false,
  btnTextColor: "",
  btnBackColor: "",
  productImage: null,
});

// Data
const form = ref<any>({
  id: "",
  title: "",
  date: null,
  activity: {},
  time: null,
  name: "",
  description: "",
  moreInfoUrl: "",
  owner: pb.authStore.model?.id || "",
  visitorEmail: pb.authStore.model?.email || "",
  numberInParty: 0,
  isRecurring: false,
  trackTickets: false,
  ticketsConfig: [createDefaultTicket()],
  optionsConfig: [],
});
const currentStep = ref<number>(0);
const showPopup = ref<boolean>(false);
const actualEndTime = ref<any>("");
const isDeleteDialogVisible = ref<boolean>(false);
const isCancelDialogVisible = ref<boolean>(false);
const recurring = ref<any>({
  frequency: 1,
  interval: "Weeks",
  days: [],
  endDate: "",
});

// computed
const eventSteps = computed(() => [
  {
    id: 1,
    title: "Date & Time",
    subtitle: "",
    icon: "tabler-calendar",
    size: '30',
    isValid: true,
  },
  {
    id: 2,
    title: "Description",
    subtitle: "",
    icon: "tabler-users",
    size: '30',
    isValid: true,
  },
  ...(!form.value.trackTickets && currentLocationDetails.value?.numberInPartyField
    ? [
        {
          id: 7,
          title: "Party Size",
          subtitle: "",
          icon: "tabler-users-group",
          size: '30',
          isValid: true,
        },
      ]
    : []),
  ...(form.value.isRecurring
    ? [
        {
          id: 3,
          title: "Recurring Event",
          subtitle: "",
          icon: "tabler-repeat",
          size: '30',
          isValid: true,
        },
      ]
    : []),
  ...(!form.value.trackTickets
    ? [
        {
          id: 4,
          title: "Payment",
          icon: "tabler-cards",
          size: '30',
          isValid: false,
        },
      ]
    : [
        {
          id: 5,
          title: "Track Tickets",
          subtitle: "Ticket configuration",
          icon: "tabler-ticket",
          size: '30',
          isValid: true,
        },
        {
          id: 6,
          title: "Options",
          subtitle: "Extra details",
          icon: "mdi-format-list-bulleted",
          size: '30',
          isValid: true,
        },
      ]),
]);

// Computed
const haveEditAccess = computed(() => {
  return props?.permission?.includes("Edit");
});

const haveCreateAccess = computed(() => {
  return props?.permission?.includes("Create");
});

const isEditEvent = computed(() => !!props?.event?.id)

const bookingStatus = computed(() => props?.event?.status);


/**
 * Computed property to generate the global info message for the booking.
 *
 * @returns {Object} - The global info message object with properties: color, icon, message.
 */
const globalInfoMessage = computed(() => {
  const isDue = dueAmount.value > 0 || props?.event?.due;

  const baseMessage = {
    color: "dialogHeaderColor",
    message: `$${dueAmount.value} - for event.`,
    icon: '',
  };

  if (isEditEvent.value) {
    const statusColorName = isDue
      ? bookingStatus.value === "futureBooking"
        ? "warning"
        : "error"
      : "dialogHeaderColor";

    const statusIcon = isDue
      ? bookingStatus.value === "futureBooking"
        ? "tabler-alert-triangle"
        : "tabler-xbox-x"
      : "";

    return {
      ...baseMessage,
      color: statusColorName,
      icon: statusIcon,
      message: isDue
        ? `The Session fee of $${dueAmount.value} has not been paid yet`
        : props?.event?.payMethod?.length > 1
        ? `Booking paid by ${props.event.payMethod.join(", ")}`
        : props?.event?.payMethod?.length === 1 &&
          props.event.payMethod[0] === "Fee Waved"
        ? "Booking fee has been waved."
        : "The Session has been fully paid.",
    };
  }

  return baseMessage;
});


/**
 * Fetches due and discount data for the payment step.
 * Handles multiple activities by making a separate API call for each activity.
 * @returns Promise with event calculation data
 */
const fetchPaymentStepData = async () => {
  try {
    isLoading.value = true;

    // Validate required fields
    if (!form.value.activity || isEmpty(form.value.activity)) {
      isLoading.value = false;
      return;
    }

    if (!form.value.date || !form.value.time || !form.value.duration) {
      isLoading.value = false;
      return;
    }

    // Convert duration to minutes (events store duration in hours)
    const durationInMinutes = form.value.duration * 60;

    // Get all activities from the activity object
    // Events store activity as: {activityId1: [bay1, bay2], activityId2: [bay3, bay4]}
    const activityKeys = Object.keys(form.value.activity);
    if (activityKeys.length === 0) {
      isLoading.value = false;
      return;
    }

    // Initialize aggregated values
    let totalDue = 0;
    let totalAmount = 0;

    // Track discounts by source for prioritization
    const discountSources = {
      TOD: 0,
      Membership: 0,
      DiscountCode: 0,
      Manual: 0,
      None: 0
    };

    // Process each activity separately
    const newActivityDiscounts: Record<string, any> = {};

    for (const activityId of activityKeys) {
      const baysForActivity = form.value.activity[activityId];
      const bayCount = baysForActivity.length;

      if (bayCount === 0) continue;

      // Fetch activity with TODPricing expansion
      const activityWithTOD = await pb.collection('activities').getOne(activityId, {
        expand: 'TODPricing'
      });

      // Make the API call for this activity
      const response = await pb.send('/bbapi/bookings/getBookingDue', {
        method: 'POST',
        body: {
          membership: userMembershipDiscounts.value,
          user: pb.authStore.model,
          activity: activityWithTOD,
          date: form.value.date,
          time: form.value.time,
          duration: durationInMinutes,
          options: [],
          discountCodeBookings: 0,
          discountCodeProducts: 0,
          docketBookingDiscount: 0,
          docketProductDiscount: 0,
          alreadyPaid: 0, // Don't pass alreadyPaid for individual calculations
          extraMinutes: 0,
          isEvent: true,
        },
        requestKey: null,
      });

      // Store discount data for this specific activity
      newActivityDiscounts[activityId] = {
        todAdjustment: response.todAdjustment,
        docketDiscount: response.docketDiscount,
        discountApplied: response.discountApplied,
        lineDiscountTotal: response.lineDiscountTotal,
        total: response.total,
        due: response.due
      };

      // Multiply by number of bays for this activity
      const activityTotal = response.total * bayCount;

      totalAmount += activityTotal;
      totalDue += response.due * bayCount;

      // Track discount source occurrences
      if (discountSources.hasOwnProperty(response.discountApplied)) {
        discountSources[response.discountApplied] += activityTotal;
      }
    }

    // Store the per-activity discount data
    activityDiscounts.value = newActivityDiscounts;

    totalDue = totalDue - (props?.event?.paid || 0);
    dueAmount.value = totalDue;
    eventTotal.value = totalAmount;
  } catch (error) {
    console.error('Error fetching payment step data:', error);
    handleUpdateErrorMessage(
      true,
      'Failed to load payment information. Please try again.'
    );
  } finally {
    isLoading.value = false;
  }
};

const validateArea = async () => {
  if (isEmpty(form.value.activity)) {
    handleUpdateErrorMessage(
      true,
      `Please select at least 1 ${props?.areaSingular}.`
    );
    return false;
  }
  return true;
};

/**
 * Validates the recurring configuration for a booking.
 *
 * @returns {Promise<boolean>} - Returns true if the recurring configuration is valid, false otherwise.
 */
const validateRecurring = async () => {
  // If the booking is not recurring, return true
  if (!form.value?.isRecurring) {
    return true;
  }

  // Extract the recurring configuration values
  const { interval, endDate, frequency, days } = recurring?.value;

  // Define the required fields and their corresponding error messages
  const requiredFields = [
    {
      field: interval,
      message: "Please select a recurring interval.",
    },
    {
      field: frequency,
      message: "Please specify the recurring frequency.",
    },
    {
      field: days && days?.length > 0,
      message: "Please select at least one recurring day.",
    },
    {
      field: endDate,
      message: "Please specify the recurring end time.",
    },
  ];

  // Find the missing field (if any)
  const missingField = requiredFields.find(({ field }) => !field);

  // If a missing field is found, display an error message and return false
  if (missingField) {
    handleUpdateErrorMessage(true, missingField.message);
    return false;
  }

  // If no missing fields are found, return true
  return true;
};

/**
 * Validates all forms in the booking process.
 * If any step is invalid, jumps the stepper to the first invalid step so its
 * inline field errors are visible, and shows a banner message.
 *
 * @returns {Promise<boolean>} - Returns true if all forms are valid, false otherwise.
 */
const validateForms = async () => {
  const stepValidations = [
    { id: 1, valid: (await dateTimeStepRef.value?.validate() ?? { valid: true }).valid },
    { id: 2, valid: (await descriptionStepRef.value?.validate() ?? { valid: true }).valid },
    { id: 3, valid: (await recurringStepRef.value?.validate() ?? { valid: true }).valid },
    { id: 5, valid: (await ticketsStepRef.value?.validate() ?? { valid: true }).valid },
    { id: 6, valid: (await optionsStepRef.value?.validate() ?? { valid: true }).valid },
    { id: 7, valid: (await numberInPartyStepRef.value?.validate() ?? { valid: true }).valid },
  ];

  const firstInvalidStep = stepValidations.find((step) => !step.valid);
  if (firstInvalidStep) {
    const stepIndex = eventSteps.value.findIndex((s) => s.id === firstInvalidStep.id);
    if (stepIndex !== -1) {
      currentStep.value = stepIndex;
    }
    handleUpdateErrorMessage(true, "Please fix the highlighted fields before submitting.");
    return false;
  }
  return true;
};

/**
 * Function to build the recurring details object for a booking.
 *
 * @returns {Object} - The recurring details object.
 * @property {string} id - The ID of the recurring event.
 * @property {boolean} isEditAll - Indicates whether all occurrences of the recurring event should be edited.
 * @property {string} interval - The recurring interval (e.g., Weeks, Months).
 * @property {number} frequency - The recurring frequency.
 * @property {Array} days - The recurring days.
 * @property {string} endDate - The end date of the recurring event in the format "YYYY-MM-DD".
 */
const buildRecurringDetails = () => {
  return form.value.isRecurring
    ? {
        id: props?.recurringDetails?.recurringId || recurringEventId.value,
        isEditAll: props?.recurringDetails?.isEditAll || false,
        interval: recurring.value?.interval,
        frequency: recurring.value?.frequency,
        days: recurring.value?.days,
        endDate: moment(recurring.value?.endDate).toISOString(),
        startDate: form.value?.date ? moment(form.value?.date).toISOString() : moment().toISOString()
      }
    : {};
};

/**
 * Function to build the booking details object.
 *
 * @returns {Object} - The booking details object.
 * @property {Object} activity - The selected activities.
 * @property {string} date - The booking date in the format "YYYY-MM-DD".
 * @property {string} time - The booking time.
 * @property {number} duration - The booking duration in minutes.
 * @property {string} owner - The owner of the booking.
 * @property {string} name - The name of the booking.
 * @property {string} description - The description of the booking.
 * @property {string} moreInfoUrl - The more info link for the booking.
 * @property {string} updatedBy - The user ID of the person who updated the booking.
 * @property {boolean} isRecurring - Indicates whether the booking is recurring.
 * @property {Object} recurring - The recurring details of the booking.
 * @property {boolean} trackTickets - Indicates whether tickets are being tracked for the booking.
 * @property {string} ticketsConfig - The list of the tickets products for the booking.
 * @property {number} ticketLimit - The ticket limit for the booking.
 * @property {string} loggedInUser - The user ID of the logged-in user.
 */
const buildBookingDetails = (): object => {
  return {
    activity: form.value.activity,
    date: form.value?.date,
    due: dueAmount.value,
    time: form.value.time,
    duration: form.value.duration,
    owner: form.value.owner,
    visitorEmail: form.value.visitorEmail,
    numberInParty: form.value.trackTickets ? 0 : form.value.numberInParty,
    name: form.value.name,
    description: form.value.description,
    moreInfoUrl: form.value.moreInfoUrl,
    updatedBy: props?.userId,
    isRecurring: form.value.isRecurring,
    recurring: buildRecurringDetails(),
    trackTickets: form.value?.trackTickets,
    loggedInUser: props?.userId,
    ticketsConfig: form.value?.ticketsConfig,
    optionsConfig: form.value?.optionsConfig,
    docketId: props?.event?.docket,
    oldTicketsConfig: tempTicketsConfig.value,
    activityDiscounts: activityDiscounts.value // Pass per-activity discount data
  };
};

/**
 * Handles the submission of a booking.
 * Validates all forms, builds the booking details, and updates or creates the event.
 *
 * @returns {Promise<void>}
 */
const handleSubmit = async () => {
  // Validate forms and exit if invalid
  if (!(await validateForms()) || !(await validateRecurring())) return;
  const ticketConfig = cloneDeep(form.value.ticketsConfig);

  showPopup.value = false;
  const bookingDetails = buildBookingDetails();
  const lastStepIndex = eventSteps.value.length - 1;

  try {
    isLoading.value = true;

    // Clean up any pending one-off booking before creating recurring series
    if (form.value.isRecurring && eventId.value && !isEditEvent.value) {
      await removeExistingPendingBooking();
      eventId.value = "";
      eventBookingsState.value = "";
    }

    // Process booking based on recurrence
    const result: any = await (form.value.isRecurring
      ? handleRecurringEvent(bookingDetails)
      : updateEvent(bookingDetails));

    if (!result) {
      // Reset state and clear pending timers
      eventBookingsState.value = "";

      await updateDurationOnChange(form.value.duration)

      // Determine next steps based on trackTickets
      if (!errorDateList.value.length) {
        // Case 1: Event ticket create - Go to last step if no ticketsConfig yet, otherwise wait for ticketConfig data
        // Case 2: Event ticket edit - Close dialog if valid ticketsConfig; otherwise, go to last step

        const hasEventId = props?.event?.id;
        const hasTicketsConfig = !isEmpty(ticketConfig);

        if (hasEventId && hasTicketsConfig && form?.value?.trackTickets) {
          emit("closeDialog", false);
        } else {
          if (currentStep.value !== lastStepIndex) {
            currentStep.value = lastStepIndex;
          } else {
            emit("closeDialog", false);
          }
        }
      }
    } else {
      handleErrors(result);
      logDebug({
        description: "Result from updateEvent or handleRecurringEvent",
        callingFunction: "handleSubmit",
        error: JSON.stringify(result),
      })
    }
  } catch (e) {
    handleErrors(e); // Handle any errors
    logDebug({
      description: "Catch any error",
      callingFunction: "handleSubmit",
      error: JSON.stringify(e),
    })
  } finally {
    isLoading.value = false;
  }
};

const createUpdateRecurringBookings = async () => {
  /**
   * Function to create or update recurring bookings.
   *
   * @returns {Promise<void>}
   */
  // Clean up any pending one-off booking before creating recurring series
  console.log('eventId', eventId.value, isEditEvent.value)
  if (eventId.value && !isEditEvent.value) {
    await removeExistingPendingBooking();
    eventId.value = "";
    eventBookingsState.value = "";
  }

  // Build the booking details object
  const bookingDetails = buildBookingDetails();

  try {
    // Handle the recurring event
    await handleRecurringEvent(bookingDetails);

    // If there are no error dates, go to the agreement tab
    if (!errorDateList.value.length) {
      currentStep.value++;
    }
  } catch (e) {
    // Handle any errors that occur during the process
    handleErrors(e);
  }
};

const handleErrors = (error: any) => {
  const { message } = error?.response || {};
  if (message) {
    handleUpdateErrorMessage(true, message);
  }
};

const confirmCloseBooking = async () => {
  // check and remove pending state booking
  await removeExistingPendingBooking();
  emit("closeDialog", false);
};

const closeDialog = async () => {
  if (
    isEditEvent.value ||
    ![0, 1].includes(currentStep.value) ||
    eventBookingsState.value !== "Pending"
  ) {
    emit("closeDialog", false);
  } else {
    isCancelDialogVisible.value = true;
  }
};


const goToNextStep = async () => {
  /**
   * Function to handle the navigation to the next step in the booking process.
   *
   * @returns {Promise<void>}
   */
  try {
    const validateBookingDateTime = async () => {
      // Validate the booking date, time, area, duration, and owner.
      const { valid } = await dateTimeStepRef.value?.validate() ?? { valid: false };
      if (valid) {
        if (!isEditEvent.value && form.value.date && form.value.time) {
          const selectedDateTime = moment(`${form.value.date} ${form.value.time}`, "YYYY-MM-DD HH:mm");
          if (selectedDateTime.isBefore(moment())) {
            handleUpdateErrorMessage(true, "The selected date and time is in the past. Please choose a future time.");
            return;
          }
        }
        if (!(await validateArea())) {
          return;
        }
        if (!isEditEvent.value && !form.value?.isRecurring && !eventId.value) {
          try {
            // Reserve a slot for the booking.
            await reserveSlot({
              date: form.value?.date,
              time: form.value.time,
              duration: form.value.duration,
              owner: form.value.owner,
              visitorEmail: form.value.visitorEmail,
              activity: form.value.activity,
              userId: props?.userId,
              trackTickets: form?.value?.trackTickets,
              due: dueAmount.value,
            });
            eventBookingsState.value = "Pending";
            currentStep.value++;
            // Clear the error message.
            handleUpdateErrorMessage(false, "");
          } catch (e: any) {
            const { message } = e?.response;
            if (message) {
              handleUpdateErrorMessage(true, message);
            }
          }
        } else {
          currentStep.value++;
        }
      }
    };

    const stepId = eventSteps.value[currentStep.value]?.id;
    if (stepId === 1) {
      // Validate Date, time, area, duration, visitorEmail which is in the Date & Time step.
      await validateBookingDateTime();
    } else if (stepId === 2) {
      // Validate name and description in the Description step.
      let { valid } = await descriptionStepRef.value?.validate() ?? { valid: false };
      if (valid) {
        currentStep.value++;
      }
    } else if (stepId === 7) {
      // Validate number in party which is in the Party Size step.
      let { valid } = await numberInPartyStepRef.value?.validate() ?? { valid: false };
      if (valid) {
        currentStep.value++;
      }
    } else if (stepId === 3) {
      // Validate recurring configuration which is in the Recurring step.
      let { valid } = await recurringStepRef.value?.validate() ?? { valid: false };
      if (valid && (await validateRecurring())) {
        await createUpdateRecurringBookings();
      }
    } else {
      // Go to the next tab.
      currentStep.value++;
    }
  } catch (e) {}
};

const gotToPos = async (docketId: string) => {
  // move user to pos list screen
  if(docketId){
    emit("closeDialog", false);
    try {
      await router.push({
        name: "pos",
        query: {
          did: docketId,
        },
      });
    } catch (error) {
      handleUpdateErrorMessage(true, 'Unable to open the POS screen');
      logDebug({
        description: "Failed to navigate to POS",
        callingFunction: "gotToPos",
        error: JSON.stringify(error),
      })
    }
  } else {
    handleUpdateErrorMessage(true, 'Error saving docket');
    logDebug({
      description: "No docketId when going to POS",
      callingFunction: "gotToPos",
      error: 'No docketID',
    })
  }
};


const bookingsInEventStarted:any = ref([])
const bookingsActualDuration = ref(0)
const bookingStartDuration = ref(0)
const bookingSpacingRef:any = ref({})
const eventDockets = ref<any[]>([])
const bookingHasActualDuration:any = ref({})
const bookingGap:any = ref({})
const setActualDuration = ref(0)

const addActualDuration = async (newValue:any) => {
  if (bookingsInEventStarted.value.length > 0 && bookingsActualDuration.value > 0 && newValue > 0) {
    for (const b of bookingsInEventStarted.value) {
      if (bookingHasActualDuration.value[b]) {
        await pb.collection("bookings").update(b, {actualDuration: newValue})
      }
    }
  }
  // Close the Dialog
  bookingsInEventStarted.value = [];
  bookingSpacingRef.value = {}
  bookingStartDuration.value = 0
  bookingsActualDuration.value = 0
  eventDockets.value = []
  bookingHasActualDuration.value = {}
  emit('closeDialog', false)
}

const updateDurationOnChange = async (newValue:any) => {
  if (bookingsInEventStarted.value.length > 0 && bookingsActualDuration.value > 0) {

    let largestGap = 0;
    bookingsInEventStarted.value.forEach(b => {
      largestGap = largestGap < bookingGap.value[b] ? bookingGap.value[b] : largestGap
    })

    for (const b of bookingsInEventStarted.value) {
      if (bookingHasActualDuration.value[b] && bookingHasActualDuration.value[b] > 0) {
        let sessionLength = (bookingSpacingRef.value[b] * newValue) - largestGap // This is the Total Time Allowed
        if(sessionLength && sessionLength > 0) {
          if(bookingsActualDuration.value < sessionLength) {
            setActualDuration.value = sessionLength
            await pb.collection("bookings").update(b, {actualDuration: setActualDuration.value})
          } else {
            setActualDuration.value = bookingsActualDuration.value
          }
        }
      }
    }
  }
}

const resetBookingState = () => {
  bookingsInEventStarted.value = [];
  bookingSpacingRef.value = {}
  bookingStartDuration.value = 0
  bookingsActualDuration.value = 0
  eventDockets.value = []
  bookingHasActualDuration.value = {}
  bookingGap.value = {}
}

const getEventDockets = async(eventID:string) => {
  resetBookingState()
  let allBookings = await pb.collection("events").getOne(eventID, {fields:'bookings,expand.bookings,expand.bookings.expand.activity', expand:'bookings,bookings.activity'})

  if(allBookings && allBookings?.expand?.bookings && allBookings?.expand?.bookings?.length > 0){
    allBookings.expand.bookings.forEach((b:any) => {
      bookingsInEventStarted.value.push(b.id)
      bookingsActualDuration.value = b?.actualDuration || 0
      setActualDuration.value = b?.actualDuration || 0
      bookingStartDuration.value = b?.duration
      bookingSpacingRef.value[b.id] = b?.expand?.activity?.sessionDuration || 0
      bookingGap.value[b.id] = b?.expand?.activity?.bookingGap || 0
      bookingHasActualDuration.value[b.id] = b?.actualDuration ? true : false
    })
  }

  // Get All DocketLines for Bookings
  let allBookingsQuery = allBookings?.bookings?.map((booking:string) => 'booking="' + booking + '"').join('||')

  let allBookingsCheck = await pb.collection("docketLines").getFullList(
    {
      filter: 'transactionType="Event" && (' + allBookingsQuery + ')',
      expand: 'docket',
      fields: 'docket, expand.docket.docketNumber, expand.docket.status, expand.docket.id'
    })

  if(allBookingsCheck){
    let sortedResult:any = allBookingsCheck.sort((a,b) => a?.expand?.docket?.docketNumber == b?.expand?.docket?.docketNumber ? 0 : a?.expand?.docket?.docketNumber < b?.expand?.docket?.docketNumber ? 1 :-1)
    sortedResult.forEach((docketLine:any) => {
      if (!eventDockets.value.some((d: any) => d.id === docketLine.docket)) {
        eventDockets.value.push(docketLine.expand?.docket)
      }
    })
  }
}

// Watch
/**
 * Watch function to handle changes in the props.event.
 *
 * @param {Object} newValue - The new value of props.event.
 * @param {Object} oldValue - The old value of props.event.
 *
 * @returns {void}
 */
watch(
  () => props.event,
   async(newValue) => {
    if (newValue && !isEmpty(newValue)) {
      const eventData = { ...props?.event };
      const {
        id,
        startDate,
        duration,
        activity,
        name,
        description,
        owner,
        docket,
        recurringConfig,
        ticketConfig,
        visitorEmail,
        moreInfoUrl
      } = eventData;

      form.value = {
        id: id,
        date: startDate ? moment(startDate).format("YYYY-MM-DD") : null,
        duration: duration / 60,
        time: startDate ? moment(startDate).format("HH:mm") : null,
        activity: activity,
        name: name,
        description: description,
        moreInfoUrl: moreInfoUrl,
        visitorEmail: visitorEmail,
        owner: owner,
        numberInParty: eventData?.numberInParty || 0,
        isRecurring: false,
        trackTickets: ticketConfig?.trackTickets,
      };

      eventId.value = eventData.id;
      docketId.value = docket;

      getEventDockets(eventId.value)

      if (!isEmpty(recurringConfig) && props?.recurringDetails?.isEditAll) {
        form.value.isRecurring = true;
        recurring.value = {
          endDate: moment(recurringConfig?.endDate).format("YYYY-MM-DD"),

          frequency: recurringConfig?.frequency,
          interval: recurringConfig?.interval,
          days: recurringWeeks?.value
            ?.filter((week) =>
              recurringConfig?.days
                ?.split(", ")
                ?.map((day: string) => day.trim())
                ?.includes(week.value)
            )
            .map((week) => week.id),
        };
      }
    } else {
      resetBookingState()

      // for create booking
      form.value = {
        id: null,
        date: moment().format("YYYY-MM-DD"),
        duration: 1,
        time: null,
        activity: {},
        name: "",
        description: "",
        moreInfoUrl: "",
        owner: pb.authStore.model?.id || "",
        visitorEmail: pb.authStore.model?.email || "",
        numberInParty: 0,
        isRecurring: false,
      };
      currentStep.value = 0;
      showPopup.value = false;
      if (haveCreateAccess.value) {
        form.value.agreement = true;
      }
    }
  },
  { immediate: true }
);

/**
 * Watch function to handle changes in form values, date, actualDuration, actualStartTime, and globalErrorMessage.
 *
 * @param {Object} - An array containing form.value.date, form.value.actualDuration, form.value.actualStartTime, and globalErrorMessage.value.
 * @param {Function} - A callback function that handles the changes.
 * @param {Object} - The Vue instance.
 *
 * @returns {void}
 */
watch(
  () => [
    form.value.date,
    form.value.actualDuration,
    form.value.actualStartTime,
    globalErrorMessage.value,
  ],
  ([, newDuration, actualStartTime, newError]) => {
    nextTick(() => {
      // Handle change in actualDuration and actualStartTime
      if (newDuration && actualStartTime) {
        actualEndTime.value = moment(actualStartTime)
          .add(newDuration, "minutes")
          .format("YYYY-MM-DD HH:mm");
      }

      // Handle globalErrorMessage changes
      if (newError?.isVisible) {
        // Clear the error message after 2 seconds
        setTimeout(() => {
          handleUpdateErrorMessage(false, "", "", "");
        }, 2000);
      }
    });
  },
  { immediate: true }
);


watch(() => [props?.isDrawerOpen], async ([newValue]) => {
  if (newValue) {
    if (isEmpty(activityMap.value)) {
      await fetchActivities();
    }
    await fetchTotalSoldEvents(props?.event?.id);
    totalSoldEvents.value = soldEvents?.value?.length || 0;
    const list = await getTicketProducts(props?.event?.id);
    totalTicket.value = list?.totalTicket === -1 ? 'unlimited' : list?.totalTicket || 0;
    if (!productStore.reportingGroupList.length) {
      await productStore.fetchReportingGroups();
    }
    const ticketConfigs = list?.ticketConfigs?.map((t: any) => ({
      ...t,
      reportingGroup: t.reportingGroup
        ? productStore.reportingGroupList.find((g) => g.id === t.reportingGroup) ?? null
        : null,
    }));
    if (props?.event?.id) {
      form.value.ticketsConfig = ticketConfigs;
      tempTicketsConfig.value = ticketConfigs;
    } else {
      tempTicketsConfig.value = form.value.ticketsConfig;
    }

    const options = await pb.collection("eventOptions").getFullList({
      filter: `event = "${props?.event?.id}"`,
    });
    form.value.optionsConfig = options?.length ? options : [];
    if (props?.event?.docket) {
      // fetch docket data
      const docket = await pb.collection("dockets").getOne(props?.event?.docket, {
        expand: "discountCode",
        requestKey: null,
      });
      discountCode.value = docket?.discountCode;
      discountCodeData.value = docket?.expand?.discountCode;
    }

    // Fetch booking owner membership discounts and payment data
    // Read email from props.event directly to avoid depending on the props.event watcher (async) having already run
    let emailForDiscounts = props?.event?.visitorEmail;
    if (!emailForDiscounts && props?.event?.owner) {
      const ownerUser = await pb.collection("users").getOne(props?.event?.owner);
      emailForDiscounts = ownerUser?.email;
    }
    const user = emailForDiscounts ? await pb.collection("users").getFirstListItem(`email = "${emailForDiscounts}"`) : null;

    userMembershipDiscounts.value = await getMembershipDiscounts(user?.id ?? '');

    // Fetch payment data after membership discounts are loaded
    await fetchPaymentStepData();
  }},
  { immediate: true }
);

// Watch for changes in form fields that affect payment
watch(
  [
    () => form.value.duration,
    () => form.value.visitorEmail,
    () => form.value.time,
    () => form.value.date,
    () => form.value.activity,
  ],
  async (newValues, oldValues) => {
    if(props?.isDrawerOpen){
      // If owner changed, wait for membership discounts to update first
      if(newValues[1] !== oldValues?.[1]) {
        const user = form?.value?.visitorEmail ? await pb.collection("users").getFirstListItem(`email = "${form?.value?.visitorEmail}"`) : null;
        userMembershipDiscounts.value = await getMembershipDiscounts(user?.id || '');
      }
      await fetchPaymentStepData();
    }
  }
);

// When the drawer is closed
watch(() => props?.isDrawerOpen,(newValue) => {
    if (!newValue) {

      // Reset form values after dialog closing animation finishes
      setTimeout(() => {
        if (!props?.isDrawerOpen) {
          currentStep.value = 0;
          isDeleteDialogVisible.value = false;
          isCancelDialogVisible.value = false;
          eventId.value = "";
          eventBookingsId.value = [];
          form.value.activity = {};
          form.value = {
            id: "",
            title: "",
            date: null,
            activity: {},
            time: null,
            name: "",
            duration: 1,
            description: "",
            moreInfoUrl: "",
            owner: pb.authStore.model?.id || "",
            visitorEmail: "",
            isRecurring: false,
            trackTickets: false,
            ticketsConfig: [createDefaultTicket()],
            optionsConfig: [],
          };
          showPopup.value = false;
          recurringEventId.value = "";
          recurring.value = {
            frequency: 1,
            interval: "Weeks",
            days: [],
            endDate: "",
          };
          errorDateList.value = [];
          isBookingWarningDialogVisible.value = false;
          eventBookingsState.value = "";
          discountCode.value = "";
        }
      }, 300);
    }
  },
  { immediate: true }
);

const docketinReceipt = ref('')
const showReceiptDialog = ref(false)
const openReceipt = (docket:any) => {
  if(docket.status === "Paid" || docket.status === "Closed") {
    docketinReceipt.value = docket.id
    showReceiptDialog.value = true
  } else {
    // go to pos to pay
    emit("closeDialog", false);

    router.push({
      name: "pos",
      query: {
        did: docket.id,
      },
    });
  }
}

</script>

<template>
  <VDialog
    :model-value="props.isDrawerOpen"
    temporary
    persistent
    no-click-animation
    location="end"
    width="950"
    min-height="500"
    class="scrollable-content"
  >
    <VOverlay
      v-model="isLoading"
      contained
      persistent
      class="align-center justify-center"
    >
      <VProgressCircular indeterminate />
    </VOverlay>

    <VCard height="auto" flat class="event_stepper">
      <div
        class="d-flex align-center justify-space-between bg-dialogHeaderColor"
      >
        <!-- 👉 Header -->
        <VCardTitle>
          {{ isEditEvent ? "Update Event" : "Add Event" }}
        </VCardTitle>
        <VBtn
          icon="mdi-close"
          size="large"
          variant="text"
          class="rounded-0"
          color=""
          @click="closeDialog"
        ></VBtn>
      </div>

      <VCardText>
        <VRow>
          <!-- large screen stepper -->
          <VCol cols="12" class="stepper-form-header">
            <CustomStepper
              v-model:current-step="currentStep"
              :items="eventSteps"
              :is-active-step-valid="!isEditEvent ? false : undefined"
              class="stepper-icon-step-bg"
              direction="horizontal"
            />
          </VCol>
          <VCol cols="12" class="pt-0">
            <VWindow v-model="currentStep" class="disable-tab-transition">
              <EventStepDateTime
                ref="dateTimeStepRef"
                :form="form"
                :activity-list="props?.activityList || []"
                :area-list="props?.areaList || []"
                :area-plural="props?.areaPlural"
                :area-singular="props?.areaSingular"
                :member-list="props?.memberList || []"
                :is-edit-event="isEditEvent"
                :have-edit-access="haveEditAccess"
                :have-create-access="haveCreateAccess"
                :is-drawer-open="props?.isDrawerOpen"
                :bookings-actual-duration="bookingsActualDuration"
                :set-actual-duration="setActualDuration"
                @fetch-payment="fetchPaymentStepData"
                @update:set-actual-duration="setActualDuration = $event"
                @add-actual-duration="addActualDuration"
              />
              <EventStepDescription
                ref="descriptionStepRef"
                :form="form"
              />
              <VWindowItem v-if="eventSteps?.some((el) => el.id === 7)">
                <NumberInPartyStep ref="numberInPartyStepRef" :form="form" />
              </VWindowItem>
              <EventStepRecurring
                v-if="eventSteps?.some((el) => el.id === 3)"
                ref="recurringStepRef"
                :recurring="recurring"
                :form-date="form.date"
                :location-list="props?.locationList || []"
                :current-location="props?.currentLocation"
                @update-error-message="handleUpdateErrorMessage(true, $event)"
              />
              <VWindowItem v-if="eventSteps?.some((el) => el.id === 4) && (dueAmount > 0 || props?.event?.due > 0 || currentStep >= eventSteps.length - 1)">
                <PaymentForm
                  :have-create-permission="haveCreateAccess"
                  :logged-in-user-id="props?.userId"
                  :location="props?.currentLocation"
                  :owner="form?.visitorEmail"
                  :activityMap="activityMap"
                  :event-id="eventId"
                  :totalDue="dueAmount > 0 ? dueAmount : props?.event?.due"
                  :total="eventTotal"
                  :activityDiscounts="activityDiscounts"
                  type="event"
                  @refetch="refetchEvent()"
                  @updateMessage="handleUpdateErrorMessage($event?.isVisible, $event?.message, $event?.color, $event?.icon)"
                  @paymentSuccess="async () => { await refetchEvent(); emit('fetchListEvent', true); }"
                  @goToPosPayment="gotToPos"
                  @closeDialog="emit('closeDialog', false)"
                />
              </VWindowItem>
              <EventStepTickets
                v-if="eventSteps?.some((el) => el.id === 5)"
                ref="ticketsStepRef"
                v-model="form.ticketsConfig"
                :event-name="form.name"
                :event-description="form.description"
              />
              <EventStepOptions
                v-if="eventSteps?.some((el) => el.id === 6)"
                ref="optionsStepRef"
                v-model="form.optionsConfig"
              />
            </VWindow>
          </VCol>
        </VRow>
      </VCardText>

      <!-- Global Error Message -->
      <div class="bold_tag">
        <VAlert
          v-if="!form.trackTickets"
          :color="globalErrorMessage?.isVisible ? (globalErrorMessage?.color || 'error') : globalInfoMessage.color"
          :icon="globalErrorMessage?.isVisible ? (globalErrorMessage?.icon || 'tabler-xbox-x') : (globalInfoMessage?.icon || 'mdi-info')"
        >
          <div class="d-md-flex justify-space-between gap-6">
            {{ globalErrorMessage?.isVisible ? globalErrorMessage?.message : globalInfoMessage?.message }}
            <div class="d-flex flex-wrap gap-4 align-left" v-if="eventDockets.length > 0">
              Dockets
              <a
                v-for="dck in eventDockets" :key="dck.id"
                @click.prevent="openReceipt(dck)"
                class="docket-link"
              >
                #{{dck.docketNumber}}
              </a>
            </div>
          </div>
        </VAlert>
        <VAlert
          v-else-if="props.event.ticketConfig"
          color="secondary"
          icon="mdi-ticket-confirmation"
        >
          {{
            totalSoldEvents === totalTicket
              ? `All tickets are sold out for this event.`
              : `Sold ${totalSoldEvents} out of ${totalTicket} tickets.`
          }}
          {{totalTicket === 'unlimited' ? '' : ` ${totalTicket} tickets available`}}
        </VAlert>
      </div>
      <!-- Global Error Message End -->

      <!-- Button Group -->
      <EventDialogButtons
        :current-step="currentStep"
        :is-edit-event="isEditEvent"
        :event-steps="eventSteps"
        :booking-status="bookingStatus"
        :track-tickets="form.trackTickets"
        @prev="currentStep--"
        @next="goToNextStep"
        @submit="handleSubmit"
        @delete="isDeleteDialogVisible = true"
        @close="closeDialog"
      />
      <!-- Button Group -->
    </VCard>
    <!-- Delete Booking confirm dialog-->
    <BookingDeleteConfirmDialog
      :is-open="isDeleteDialogVisible"
      :is-recurring="!!props?.recurringDetails?.bookings?.length"
      :ticketCount="totalSoldEvents"
      type="Event"
      @closeDialog="isDeleteDialogVisible = $event"
      @cancelBooking="
        async () => {
          if (props?.recurringDetails?.isEditAll && props?.recurringDetails?.recurringId) {
            await confirmDeleteAllRecurring(props.recurringDetails.recurringId);
          } else {
            await confirmDelete();
          }
          emit('closeDialog', false);
        }
      "
    />

    <!-- Booking cancel confirmation dialog -->
    <BookingCancelConfirmDialog
      :is-open="isCancelDialogVisible"
      type="Event"
      @closeDialog="isCancelDialogVisible = $event"
      @cancelBooking="confirmCloseBooking"
    />

    <!-- Booking warning dialog with dates -->
    <ErrorWarnDatesDialog
      :is-open="isBookingWarningDialogVisible"
      :dates="errorDateList"
      @closeDialog="isBookingWarningDialogVisible = false"
    />
  </VDialog>

  <dialog-receipt :docketId="docketinReceipt" v-model:showDialog="showReceiptDialog" />

</template>

<style scoped lang="scss">
.stepper-form-header {
  padding: 1.25rem;
}
</style>
